'use client';

import React, { useMemo } from 'react';
import { useGetEntityDataByCategoriesQuery } from 'src/api/redux';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import chartColorScale from 'src/modules/Profile/components/Charts/chartColorScale';

interface CategoryDatum {
  category: string;
  total_amount: number;
  payment_count: number;
  color: string;
}

const MAX_ITEMS = 8;

const CompactOpenPaymentsByCategories: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const { entityDetails } = useProfile();

  const { data, isLoading } = useGetEntityDataByCategoriesQuery(
    {
      providerId: entityDetails?.provider_id,
      company: widget?.entity?.id || undefined,
    },
    { skip: !entityDetails }
  );

  const items: CategoryDatum[] = useMemo(() => {
    if (!data?.results?.length) return [];

    return data.results
      .filter((item: { total_amount: number }) => item.total_amount > 0)
      .sort(
        (a: { total_amount: number }, b: { total_amount: number }) =>
          b.total_amount - a.total_amount
      )
      .slice(0, MAX_ITEMS)
      .map(
        (
          item: {
            category: string;
            total_amount: number;
            payment_count: number;
          },
          i: number
        ) => ({
          category: item.category,
          total_amount: item.total_amount,
          payment_count: item.payment_count,
          color: chartColorScale[i % chartColorScale.length],
        })
      );
  }, [data]);

  const totalAmount = useMemo(
    () => items.reduce((sum, d) => sum + d.total_amount, 0),
    [items]
  );

  const maxAmount = items.length > 0 ? items[0].total_amount : 0;

  return (
    <Card
      className="p-4 flex flex-col gap-4 bg-white h-full"
      data-testid="compact-open-payments-by-categories"
    >
      <WidgetHeader
        fallbackTitle="Open Payments by Category"
        trailing={
          items.length > 0 ? (
            <span
              className="text-sm font-semibold text-secondary tabular-nums"
              data-testid="total-amount"
            >
              {formatCurrency(totalAmount)} total
            </span>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <WidgetLoading data-testid="compact-open-payments-by-categories-loading" />
        ) : items.length === 0 ? (
          <WidgetEmpty data-testid="compact-open-payments-by-categories-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col gap-3"
            role="img"
            aria-label="Open payments by category"
            data-testid="open-payments-categories-chart"
          >
            {items.map((item, index) => (
              <div
                key={item.category}
                className="flex flex-col gap-1"
                data-testid={`category-row-${index}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-sm font-medium text-primary truncate"
                    title={item.category}
                  >
                    {item.category}
                  </span>
                  <span className="text-sm text-secondary tabular-nums shrink-0">
                    {formatCurrency(item.total_amount)} &middot;{' '}
                    {item.payment_count.toLocaleString()} payments
                  </span>
                </div>
                <div className="h-3 w-full rounded-full bg-neutral-100">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    data-testid={`category-bar-${index}`}
                    style={{
                      width: `${(item.total_amount / maxAmount) * 100}%`,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactOpenPaymentsByCategories;
